import { nanoid } from 'nanoid'
import type {
  BreakParams,
  BreakResult,
} from '@/plugins/cad/module/CADModule/edit/EntityEditHandler'
import { EntityType } from '@/plugins/cad/module/CADModule/model/common/constant'
import {
  ArcEntity,
  type CircleEntity,
  type EllipseEntity,
  type IEntity,
  type IPolylineVertice,
  PolylineEntity,
  type RayEntity,
  type SplineEntity,
} from '@/plugins/cad/module/CADModule/model/entity'
import { SplineUtils } from '@/plugins/cad/module/utils'
import { getLoggerManager } from '@/shared/logging'

const log = getLoggerManager().getLogger('cad')

/** B 样条投影时的采样点数（越大精度越高，开销越大） */
const SPLINE_SAMPLE_NUM = 200
/** 打断后每段样条使用的拟合点数 */
const SPLINE_FIT_NUM = 16

/**
 * 打断实体工具集合，按实体类型将其切分为新片段并标记需删除的原实体。
 */
export class BreakUtils {
  /** 支持打断的实体类型集合 */
  static readonly EnabledEntityTypes = new Set<EntityType>([
    EntityType.Polyline,
    EntityType.Arc,
    EntityType.Circle,
    EntityType.Ellipse,
    EntityType.Spline,
    EntityType.Ray,
  ])

  /**
   * 根据实体类型执行打断逻辑。
   *
   * @param entity - 要打断的实体
   * @param params - 打断参数，包含两个分割点
   * @returns 新生成与需删除的实体；不支持或失败时两列表均为空
   */
  static breakEntity(entity: IEntity, params: BreakParams): BreakResult {
    switch (entity.type) {
      case EntityType.Polyline:
        return BreakUtils.breakPolyline(
          entity as PolylineEntity,
          params.firstPoint,
          params.secondPoint
        )
      case EntityType.Arc:
        return BreakUtils.breakArc(entity as ArcEntity, params.firstPoint, params.secondPoint)
      case EntityType.Circle:
        return BreakUtils.breakCircle(entity as CircleEntity, params.firstPoint, params.secondPoint)
      case EntityType.Spline:
        return BreakUtils.breakSpline(entity as SplineEntity, params.firstPoint, params.secondPoint)
      case EntityType.Ellipse:
        return BreakUtils.breakEllipse(
          entity as EllipseEntity,
          params.firstPoint,
          params.secondPoint
        )
      case EntityType.Ray:
        return BreakUtils.breakRay(entity as RayEntity, params.firstPoint, params.secondPoint)
      default:
        log.warn(`Break operation not implemented for entity type: ${entity.type}`)
        return { createdEntities: [], deleteEntities: [] }
    }
  }

  // =========================================================================
  // Polyline（含 bulge 弧段）
  // =========================================================================

  /**
   * 由两端点和 bulge 推算圆弧的圆心、半径及起止角。
   * bulge = tan(α/4)，α 为有向圆心角（CCW > 0，CW < 0）。
   * 公式来源与 offsetUtils.arcFromBulge 一致。
   */
  private static arcParamsFromBulge(
    p1: { x: number; y: number },
    p2: { x: number; y: number },
    bulge: number
  ): { cx: number; cy: number; radius: number; startAngle: number; endAngle: number } {
    const dx = p2.x - p1.x,
      dy = p2.y - p1.y
    const L = Math.hypot(dx, dy)
    const mx = (p1.x + p2.x) / 2,
      my = (p1.y + p2.y) / 2
    const nx = -dy / L,
      ny = dx / L // 弦法线（CCW）
    const d_c = ((L / 2) * (1 - bulge * bulge)) / (2 * bulge)
    const cx = mx + nx * d_c
    const cy = my + ny * d_c
    const radius = Math.hypot(L / 2, d_c)
    const startAngle = Math.atan2(p1.y - cy, p1.x - cx)
    const endAngle = Math.atan2(p2.y - cy, p2.x - cx)
    return { cx, cy, radius, startAngle, endAngle }
  }

  /**
   * 将点投影到弧段（由 bulge 定义），返回弧参数 t∈[0,1] 及投影点坐标。
   * t=0 对应 p1，t=1 对应 p2。
   */
  private static projectPointOnArcSegment(
    p: { x: number; y: number },
    p1: { x: number; y: number },
    p2: { x: number; y: number },
    bulge: number
  ): { t: number; point: { x: number; y: number } } {
    const { cx, cy, radius, startAngle } = BreakUtils.arcParamsFromBulge(p1, p2, bulge)

    // 点到圆心方向角
    const angle = Math.atan2(p.y - cy, p.x - cx)

    // 将 angle 夹紧到 [startAngle, endAngle]（有方向）
    // 有向总扫角 sweep = 4 * atan(bulge)（正 CCW，负 CW）
    const sweep = 4 * Math.atan(bulge)

    // 相对于 startAngle 的有向偏移（映射到与 sweep 同号的范围）
    let delta = angle - startAngle
    // 归一化 delta 到 [0, 2π) 或 (-2π, 0]，与 sweep 同号
    if (sweep > 0) {
      while (delta < 0) delta += Math.PI * 2
      while (delta > Math.PI * 2) delta -= Math.PI * 2
    } else {
      while (delta > 0) delta -= Math.PI * 2
      while (delta < -Math.PI * 2) delta += Math.PI * 2
    }

    // 夹紧到 [0, sweep]
    const t = Math.max(0, Math.min(1, delta / sweep))
    const clampedAngle = startAngle + t * sweep
    return {
      t,
      point: { x: cx + radius * Math.cos(clampedAngle), y: cy + radius * Math.sin(clampedAngle) },
    }
  }

  /**
   * 在弧段上取 t∈[0,1] 处的点。
   * 同时返回该点作为新端点时，前半弧 [0,t] 的 bulge 值和后半弧 [t,1] 的 bulge 值。
   *
   * 新 bulge 推导：
   *   sweep_full = 4 * atan(bulge)
   *   sweep_part = sweep_full * t（或 (1-t)）
   *   new_bulge  = tan(sweep_part / 4)
   */
  private static splitArcSegment(
    p1: { x: number; y: number },
    p2: { x: number; y: number },
    bulge: number,
    t: number // 分割点参数 [0,1]
  ): {
    splitPoint: { x: number; y: number }
    bulge1: number // p1 → splitPoint 的 bulge
    bulge2: number // splitPoint → p2 的 bulge
  } {
    const { cx, cy, radius, startAngle } = BreakUtils.arcParamsFromBulge(p1, p2, bulge)
    const sweep = 4 * Math.atan(bulge)
    const midAngle = startAngle + t * sweep
    const splitPoint = { x: cx + radius * Math.cos(midAngle), y: cy + radius * Math.sin(midAngle) }
    const bulge1 = Math.tan((sweep * t) / 4)
    const bulge2 = Math.tan((sweep * (1 - t)) / 4)
    return { splitPoint, bulge1, bulge2 }
  }

  /**
   * 计算点在折线上的参数 t（范围 [0, vertices.length-1]）。
   * 对弧段（bulge ≠ 0）使用弧段投影；对直线段使用线段投影。
   * t = i + frac，其中 i 是段索引，frac ∈ [0,1] 是段内参数。
   */
  private static projectPointOnPolyline(
    p: { x: number; y: number },
    vertices: IPolylineVertice[]
  ): { t: number; point: { x: number; y: number } } {
    let bestT = 0
    let bestDistSq = Infinity
    let bestPoint: { x: number; y: number } = { ...vertices[0] }

    for (let i = 0; i < vertices.length - 1; i++) {
      const v1 = vertices[i],
        v2 = vertices[i + 1]
      const bulge = v1.bulge ?? 0

      let t: number
      let point: { x: number; y: number }

      if (Math.abs(bulge) < 1e-10) {
        // 直线段
        const dx = v2.x - v1.x,
          dy = v2.y - v1.y
        const lenSq = dx * dx + dy * dy
        if (lenSq < 1e-12) {
          t = 0
          point = { x: v1.x, y: v1.y }
        } else {
          const raw = ((p.x - v1.x) * dx + (p.y - v1.y) * dy) / lenSq
          t = Math.max(0, Math.min(1, raw))
          point = { x: v1.x + t * dx, y: v1.y + t * dy }
        }
      } else {
        // 弧段
        const result = BreakUtils.projectPointOnArcSegment(p, v1, v2, bulge)
        t = result.t
        point = result.point
      }

      const dx = p.x - point.x,
        dy = p.y - point.y
      const distSq = dx * dx + dy * dy
      if (distSq < bestDistSq) {
        bestDistSq = distSq
        bestT = i + t
        bestPoint = point
      }
    }
    return { t: bestT, point: bestPoint }
  }

  /**
   * 从折线中裁剪 [tStart, tEnd] 段，返回保留正确 bulge 的 IPolylineVertice 数组。
   *
   * 规则：
   * - 完整经过的段直接复制顶点（bulge 不变）
   * - 起始段若从中间切入，对弧段拆分求新 bulge；对直线段线性插值，bulge=0
   * - 结束段若在中间截止，同上
   */
  private static clipPolylineSegment(
    vertices: IPolylineVertice[],
    tStart: number,
    tEnd: number
  ): IPolylineVertice[] {
    if (tEnd - tStart < 1e-10) return []
    const n = vertices.length
    const result: IPolylineVertice[] = []

    const iStart = Math.min(Math.floor(tStart), n - 2)
    const iEnd = Math.min(Math.floor(tEnd), n - 2)
    const fracStart = tStart - iStart

    // ── 起点 ──────────────────────────────────────────────────────────────
    const v1s = vertices[iStart],
      v2s = vertices[iStart + 1]
    const bulgeS = v1s.bulge ?? 0

    let startPt: IPolylineVertice
    if (fracStart < 1e-10) {
      // 恰好落在顶点上
      startPt = { ...v1s }
    } else if (Math.abs(bulgeS) < 1e-10) {
      // 直线段插值，bulge=0
      startPt = {
        x: v1s.x + (v2s.x - v1s.x) * fracStart,
        y: v1s.y + (v2s.y - v1s.y) * fracStart,
        bulge: 0,
      }
    } else {
      // 弧段：拆分，取后半弧的起点
      const { splitPoint, bulge2 } = BreakUtils.splitArcSegment(v1s, v2s, bulgeS, fracStart)
      // 起点不带 bulge（它是本段新端点，bulge 归属于下一段顶点处）
      // 实际上 bulge 记录在"该顶点到下个顶点"的弧参，所以 startPt.bulge = bulge2
      startPt = { x: splitPoint.x, y: splitPoint.y, bulge: bulge2 }
    }
    result.push(startPt)

    // ── 中间完整顶点（iStart+1 .. iEnd，不含终点段的末尾顶点） ───────────
    if (iStart === iEnd) {
      // 起点和终点在同一段内
      const iE = Math.floor(tEnd)
      const actualFracEnd = tEnd - iE

      if (actualFracEnd < 1e-10) {
        // 终点正好是顶点 iE
        if (iE > iStart) result.push({ ...vertices[iE] })
      } else {
        // 终点在同一段内的 actualFracEnd 处
        const bulge0 = vertices[iStart].bulge ?? 0
        if (Math.abs(bulge0) < 1e-10) {
          const v2 = vertices[iStart + 1]
          result.push({
            x: vertices[iStart].x + (v2.x - vertices[iStart].x) * actualFracEnd,
            y: vertices[iStart].y + (v2.y - vertices[iStart].y) * actualFracEnd,
            bulge: 0,
          })
        } else {
          // 弧段内截一段 [fracStart, fracEndTotal] = [fracStart, fracStart + (tEnd-tStart)]
          // 先对整段 bulge0 做参数变换：t_in_full_seg = fracStart..actualFracEnd
          // 先分割出 [0..fracStart] 后半 → 再从中取 [0..((actualFracEnd-fracStart)/(1-fracStart))]
          const { splitPoint: sp1, bulge2: b_remain } = BreakUtils.splitArcSegment(
            vertices[iStart],
            vertices[iStart + 1],
            bulge0,
            fracStart
          )
          // 此时剩余弧为 sp1→v2，bulge = b_remain，参数在剩余弧上的位置
          const tInRemain = (actualFracEnd - fracStart) / (1 - fracStart)
          const { splitPoint: endPt, bulge1: b_clip } = BreakUtils.splitArcSegment(
            sp1,
            vertices[iStart + 1],
            b_remain,
            Math.max(0, Math.min(1, tInRemain))
          )
          // 修正 result[0] 的 bulge 为裁剪后的值
          result[0] = { ...result[0], bulge: b_clip }
          result.push({ x: endPt.x, y: endPt.y, bulge: 0 })
        }
      }
      return result.length >= 2 ? result : []
    }

    // 起点所在段的终点（iStart+1），需更新 result[0].bulge
    if (Math.abs(bulgeS) < 1e-10 || fracStart < 1e-10) {
      // 直线段 / 无需拆分：bulge 不变，补充段末端点
      // result[0].bulge 已正确（直线=0 或完整弧的 bulge=bulgeS）
      if (fracStart > 1e-10) {
        // 已从中间起始：result[0].bulge 已由弧段拆分设置，无需再添加 iStart+1
      }
      // 若 fracStart < 1e-10，result[0] 就是 v1s，其 bulge 正确指向 v2s，也无需额外处理
    }

    // ── 中间完整顶点（iStart+1 .. iEnd） ────────────────────────────────
    for (let i = iStart + 1; i <= iEnd; i++) {
      result.push({ ...vertices[i] })
    }

    // ── 终点 ──────────────────────────────────────────────────────────────
    const actualFracEnd = tEnd - iEnd
    const v1e = vertices[iEnd],
      v2e = vertices[iEnd + 1]
    const bulgeE = v1e.bulge ?? 0

    if (actualFracEnd < 1e-10) {
      // 终点恰好是 iEnd 这个顶点，已被上面的循环添加，无需额外添加
    } else if (Math.abs(bulgeE) < 1e-10) {
      // 直线段终点插值
      // 先修正 result 最后一个顶点（即 v1e）的 bulge 应为 0（已是直线）
      result[result.length - 1] = { ...v1e, bulge: 0 }
      result.push({
        x: v1e.x + (v2e.x - v1e.x) * actualFracEnd,
        y: v1e.y + (v2e.y - v1e.y) * actualFracEnd,
        bulge: 0,
      })
    } else {
      // 弧段：保留 [0, actualFracEnd] 部分，bulge = bulge1
      const { splitPoint, bulge1 } = BreakUtils.splitArcSegment(v1e, v2e, bulgeE, actualFracEnd)
      // 更新终点段起始顶点的 bulge 为裁剪后的值
      result[result.length - 1] = { ...v1e, bulge: bulge1 }
      result.push({ x: splitPoint.x, y: splitPoint.y, bulge: 0 })
    }

    return result.length >= 2 ? result : []
  }

  private static breakPolyline(
    entity: PolylineEntity,
    p1: { x: number; y: number },
    p2: { x: number; y: number }
  ): BreakResult {
    const vertices = entity.vertices
    if (vertices.length < 2) return { createdEntities: [], deleteEntities: [] }

    return entity.isClosed
      ? BreakUtils.breakClosedPolyline(entity, p1, p2)
      : BreakUtils.breakOpenPolyline(entity, p1, p2)
  }

  /**
   * 将开放折线裁剪到 [tStart, tEnd] 区间，返回一条新的开放折线实体。
   *
   * @param entity  要裁剪的开放折线
   * @param tStart  起始参数
   * @param tEnd    终止参数
   * @returns 裁剪后的开放折线实体，若裁剪结果不足两点则返回 null
   */
  static clipOpenPolylineToInterval(
    entity: PolylineEntity,
    tStart: number,
    tEnd: number
  ): PolylineEntity | null {
    if (entity.isClosed || entity.vertices.length < 2) return null
    const maxT = entity.vertices.length - 1
    const clipped = BreakUtils.clipPolylineSegment(
      entity.vertices,
      Math.max(0, Math.min(tStart, maxT)),
      Math.max(0, Math.min(tEnd, maxT))
    )
    if (clipped.length < 2) return null
    const newEntity = entity.clone() as PolylineEntity
    Object.assign(newEntity, { id: nanoid(), vertices: clipped, isClosed: false })
    return newEntity
  }

  /**
   * 开放多段线打断：
   * 移除 [tStart, tEnd] 段，保留前后各一条开放多段线（最多两条）。
   */
  private static breakOpenPolyline(
    entity: PolylineEntity,
    p1: { x: number; y: number },
    p2: { x: number; y: number }
  ): BreakResult {
    const results: BreakResult = { createdEntities: [], deleteEntities: [] }
    const vertices = entity.vertices

    const { t: t1 } = BreakUtils.projectPointOnPolyline(p1, vertices)
    const { t: t2 } = BreakUtils.projectPointOnPolyline(p2, vertices)
    const tStart = Math.min(t1, t2)
    const tEnd = Math.max(t1, t2)
    const maxT = vertices.length - 1

    const buildSeg = (from: number, to: number): PolylineEntity | null => {
      const seg = BreakUtils.clipPolylineSegment(vertices, from, to)
      if (seg.length < 2) return null
      const e = entity.clone() as PolylineEntity
      Object.assign(e, { id: nanoid(), vertices: seg, isClosed: false })
      return e
    }

    if (tStart > 1e-6) {
      const seg = buildSeg(0, tStart)
      if (seg) results.createdEntities.push(seg)
    }
    if (tEnd < maxT - 1e-6) {
      const seg = buildSeg(tEnd, maxT)
      if (seg) results.createdEntities.push(seg)
    }

    if (results.createdEntities.length > 0) results.deleteEntities.push(entity)
    return results
  }

  /**
   * 闭合多段线打断：
   * 将参数空间展开到 [0, n]（n = 顶点数 = 段数，含闭合段），
   * 移除 [tStart, tEnd]，将剩余部分 [tEnd, n] + [0, tStart] 拼接为一条开放多段线。
   *
   * 无论两打断点位置如何，结果始终为一条（或零条）开放多段线。
   */
  private static breakClosedPolyline(
    entity: PolylineEntity,
    p1: { x: number; y: number },
    p2: { x: number; y: number }
  ): BreakResult {
    const results: BreakResult = { createdEntities: [], deleteEntities: [] }
    const vertices = entity.vertices
    const n = vertices.length // 段数 = 顶点数（闭合）

    // 展开：末尾追加首顶点副本，使参数范围变为 [0, n]
    // extendedVertices[n-1].bulge 保存了闭合段的弧参数
    const extendedVertices: IPolylineVertice[] = [
      ...vertices,
      { x: vertices[0].x, y: vertices[0].y, bulge: 0 },
    ]

    const { t: t1 } = BreakUtils.projectPointOnPolyline(p1, extendedVertices)
    const { t: t2 } = BreakUtils.projectPointOnPolyline(p2, extendedVertices)
    const tStart = Math.min(t1, t2)
    const tEnd = Math.max(t1, t2)
    const maxT = n
    const EPS = 1e-6

    if (tEnd - tStart < EPS) return results

    // 后半段 [tEnd, maxT]：从第二打断点绕回首顶点
    const rawSeg2 =
      tEnd < maxT - EPS ? BreakUtils.clipPolylineSegment(extendedVertices, tEnd, maxT) : null

    // 前半段 [0, tStart]：从首顶点到第一打断点
    const rawSeg1 =
      tStart > EPS ? BreakUtils.clipPolylineSegment(extendedVertices, 0, tStart) : null

    let combined: IPolylineVertice[] = []

    if (rawSeg2 && rawSeg2.length >= 1 && rawSeg1 && rawSeg1.length >= 1) {
      const last = rawSeg2[rawSeg2.length - 1]
      const first = rawSeg1[0]
      if (Math.abs(last.x - first.x) < EPS && Math.abs(last.y - first.y) < EPS) {
        // 拼接处坐标重合（均为 vertices[0]）：
        // 保留 rawSeg1[0] 的 bulge（表示从 v0 出发的弧参），丢弃 rawSeg2 末尾的 bulge=0
        combined = [...rawSeg2.slice(0, -1), first, ...rawSeg1.slice(1)]
      } else {
        combined = [...rawSeg2, ...rawSeg1]
      }
    } else if (rawSeg2) {
      combined = rawSeg2
    } else if (rawSeg1) {
      combined = rawSeg1
    }

    // 无论是否有新实体，原闭合多段线都应被删除（打断已发生）
    results.deleteEntities.push(entity)

    if (combined.length >= 2) {
      const newEntity = entity.clone() as PolylineEntity
      Object.assign(newEntity, { id: nanoid(), vertices: combined, isClosed: false })
      results.createdEntities.push(newEntity)
    }

    return results
  }

  private static normalizeAngle(angle: number): number {
    const TWO_PI = Math.PI * 2
    return ((angle % TWO_PI) + TWO_PI) % TWO_PI
  }

  /** 将点投影到以 center 为圆心的圆上，返回角度（弧度，已归一化到 [0, 2π)） */
  private static projectAngleOnCircle(
    p: { x: number; y: number },
    center: { x: number; y: number }
  ): number {
    return BreakUtils.normalizeAngle(Math.atan2(p.y - center.y, p.x - center.x))
  }

  /** 判断角度 angle 是否落在 CCW 弧 [arcStart, arcEnd] 上 */
  private static isAngleOnArc(angle: number, arcStart: number, arcEnd: number): boolean {
    const a = BreakUtils.normalizeAngle(angle)
    const s = BreakUtils.normalizeAngle(arcStart)
    const e = BreakUtils.normalizeAngle(arcEnd)
    if (s <= e) return a >= s && a <= e
    return a >= s || a <= e
  }

  /** CCW 方向从 from 到 to 的角度跨度 */
  private static ccwSpan(from: number, to: number): number {
    return BreakUtils.normalizeAngle(to - from)
  }

  /** 按圆弧方向计算从 from 到 to 的参数跨度 */
  private static directedSpan(from: number, to: number, clockwise: boolean): number {
    return clockwise ? BreakUtils.ccwSpan(to, from) : BreakUtils.ccwSpan(from, to)
  }

  /** 计算两个角度之间最短角距 */
  private static angularDistance(a: number, b: number): number {
    const span = BreakUtils.ccwSpan(a, b)
    return Math.min(span, Math.PI * 2 - span)
  }

  private static breakArc(
    entity: ArcEntity,
    p1: { x: number; y: number },
    p2: { x: number; y: number }
  ): BreakResult {
    const { center, startAngle, endAngle, clockwise } = entity
    const arcStart = BreakUtils.normalizeAngle(startAngle)
    const arcEnd = BreakUtils.normalizeAngle(endAngle)
    const arcSweep = BreakUtils.directedSpan(arcStart, arcEnd, clockwise)
    const EPS = 1e-6
    const results: BreakResult = { createdEntities: [], deleteEntities: [] }

    if (arcSweep < EPS) return results

    const clampToArc = (a: number): number => {
      const normalized = BreakUtils.normalizeAngle(a)
      if (BreakUtils.directedSpan(arcStart, normalized, clockwise) <= arcSweep + EPS) {
        return normalized
      }
      return BreakUtils.angularDistance(normalized, arcStart) <=
        BreakUtils.angularDistance(normalized, arcEnd)
        ? arcStart
        : arcEnd
    }

    let a1 = clampToArc(BreakUtils.projectAngleOnCircle(p1, center))
    let a2 = clampToArc(BreakUtils.projectAngleOnCircle(p2, center))

    // 保证 a1 在圆弧方向先于 a2（[a1, a2] 是要剔除的段）
    if (
      BreakUtils.directedSpan(arcStart, a1, clockwise) >
      BreakUtils.directedSpan(arcStart, a2, clockwise)
    ) {
      ;[a1, a2] = [a2, a1]
    }

    // 前段 [arcStart, a1]
    if (BreakUtils.directedSpan(arcStart, a1, clockwise) > EPS) {
      const newEntity = entity.clone() as ArcEntity
      Object.assign(newEntity, {
        id: nanoid(),
        startAngle: arcStart,
        endAngle: a1,
      })
      results.createdEntities.push(newEntity)
    }

    // 后段 [a2, arcEnd]
    if (BreakUtils.directedSpan(a2, arcEnd, clockwise) > EPS) {
      const newEntity = entity.clone() as ArcEntity
      Object.assign(newEntity, {
        id: nanoid(),
        startAngle: a2,
        endAngle: arcEnd,
      })
      results.createdEntities.push(newEntity)
    }

    if (results.createdEntities.length > 0) {
      results.deleteEntities.push(entity)
    }

    return results
  }

  private static breakCircle(
    entity: CircleEntity,
    p1: { x: number; y: number },
    p2: { x: number; y: number }
  ): BreakResult {
    const { center } = entity
    const a1 = BreakUtils.projectAngleOnCircle(p1, center)
    const a2 = BreakUtils.projectAngleOnCircle(p2, center)
    const results: BreakResult = { createdEntities: [], deleteEntities: [] }

    if (Math.abs(BreakUtils.normalizeAngle(a1 - a2)) < 1e-6) return results

    // 保留从 a2 CCW 到 a1 的弧（剔除 a1→a2 段）
    const newEntity = new ArcEntity({
      ...entity.serialize(),
      id: nanoid(),
      type: EntityType.Arc,
      startAngle: a2,
      endAngle: a1,
    })
    if (newEntity) {
      results.createdEntities.push(newEntity)
      results.deleteEntities.push(entity)
    }
    return results
  }

  // =========================================================================
  // Spline
  // =========================================================================

  /**
   * 获取 B 样条的有效参数范围。
   * 对于开放均匀节点向量，有效范围为 [knots[degree], knots[n-degree]]。
   * 为避免端点处基函数退化（u=uMax 时 N(i,0,u) 全为0），uMax 收缩 1e-9。
   */
  private static getSplineParamRange(entity: SplineEntity): { uMin: number; uMax: number } {
    const { knots, degree } = entity
    const uMin = knots[degree] ?? 0
    const uMax = (knots[knots.length - degree - 1] ?? 1) - 1e-9
    return { uMin, uMax }
  }

  /**
   * 将任意点投影到样条曲线上，返回使曲线上点距 p 最近的参数 u。
   * 使用均匀采样粗搜索，不做 Newton-Raphson 细化（精度对 CAD 打断已足够）。
   */
  private static projectOnSpline(p: { x: number; y: number }, entity: SplineEntity): number {
    const { uMin, uMax } = BreakUtils.getSplineParamRange(entity)
    let bestU = uMin
    let bestDistSq = Infinity

    for (let i = 0; i <= SPLINE_SAMPLE_NUM; i++) {
      const u = uMin + (uMax - uMin) * (i / SPLINE_SAMPLE_NUM)
      const pt = SplineUtils.evaluateBSpline(u, entity.controlPoints, entity.knots, entity.degree)
      const dx = p.x - pt.x,
        dy = p.y - pt.y
      const distSq = dx * dx + dy * dy
      if (distSq < bestDistSq) {
        bestDistSq = distSq
        bestU = u
      }
    }
    return bestU
  }

  /**
   * 对样条 [uStart, uEnd] 段均匀采样 numPts+1 个点。
   */
  private static sampleSplineSegment(
    entity: SplineEntity,
    uStart: number,
    uEnd: number,
    numPts: number
  ): { x: number; y: number }[] {
    const pts: { x: number; y: number }[] = []
    for (let i = 0; i <= numPts; i++) {
      const u = uStart + (uEnd - uStart) * (i / numPts)
      pts.push(SplineUtils.evaluateBSpline(u, entity.controlPoints, entity.knots, entity.degree))
    }
    return pts
  }

  private static breakSpline(
    entity: SplineEntity,
    p1: { x: number; y: number },
    p2: { x: number; y: number }
  ): BreakResult {
    const results: BreakResult = { createdEntities: [], deleteEntities: [] }
    const { uMin, uMax } = BreakUtils.getSplineParamRange(entity)

    const u1 = BreakUtils.projectOnSpline(p1, entity)
    const u2 = BreakUtils.projectOnSpline(p2, entity)
    const uStart = Math.min(u1, u2)
    const uEnd = Math.max(u1, u2)

    const EPS = (uMax - uMin) * 1e-4

    const buildSegment = (from: number, to: number): SplineEntity | null => {
      if (to - from <= EPS) return null
      const fitPts = BreakUtils.sampleSplineSegment(entity, from, to, SPLINE_FIT_NUM)
      if (fitPts.length < 2) return null
      try {
        const { controlPoints, knots, degree } = SplineUtils.fitPointsToSpline(fitPts)
        const newEntity = entity.clone() as SplineEntity
        Object.assign(newEntity, {
          id: nanoid(),
          method: 'FIT' as const,
          fitPoints: fitPts,
          controlPoints,
          knots,
          degree,
          isClosed: false,
          startTangent: undefined,
          endTangent: undefined,
        })
        return newEntity
      } catch {
        return null
      }
    }

    // 前段 [uMin, uStart]
    const seg1 = buildSegment(uMin, uStart)
    if (seg1) results.createdEntities.push(seg1)

    // 后段 [uEnd, uMax]
    const seg2 = buildSegment(uEnd, uMax)
    if (seg2) results.createdEntities.push(seg2)

    if (results.createdEntities.length > 0) {
      results.deleteEntities.push(entity)
    }
    return results
  }

  // =========================================================================
  // Ellipse
  // =========================================================================

  /**
   * 将点投影到椭圆的参数坐标系，返回最接近的参数角 θ（归一化到 [0, 2π)）。
   *
   * 椭圆参数化：
   *   x = cx + a·cos(θ)·cos(r) − b·sin(θ)·sin(r)
   *   y = cy + a·cos(θ)·sin(r) + b·sin(θ)·cos(r)
   * 其中 a = radiusX，b = radiusY，r = rotation
   *
   * 将点变换到椭圆局部坐标后，用 atan2(v/b, u/a) 近似参数角。
   * （注：这并非真正的最短距离投影，但对 CAD 打断操作精度已足够。）
   */
  private static projectAngleOnEllipse(p: { x: number; y: number }, entity: EllipseEntity): number {
    const { center, radiusX, radiusY, rotation } = entity
    const dx = p.x - center.x,
      dy = p.y - center.y
    const cosR = Math.cos(rotation),
      sinR = Math.sin(rotation)
    // 变换到椭圆局部坐标系（旋转 -rotation）
    const u = dx * cosR + dy * sinR // 沿长轴分量
    const v = -dx * sinR + dy * cosR // 沿短轴分量
    return BreakUtils.normalizeAngle(Math.atan2(v / radiusY, u / radiusX))
  }

  private static breakEllipse(
    entity: EllipseEntity,
    p1: { x: number; y: number },
    p2: { x: number; y: number }
  ): BreakResult {
    const results: BreakResult = { createdEntities: [], deleteEntities: [] }
    const arcStart = BreakUtils.normalizeAngle(entity.startAngle)
    const arcEnd = BreakUtils.normalizeAngle(entity.endAngle)

    // 完整椭圆：CCW 跨度接近 0（startAngle≈endAngle 绕了一圈）
    const isFullEllipse = BreakUtils.ccwSpan(arcStart, arcEnd) < 1e-6

    if (isFullEllipse) {
      // 类似圆：保留 [a2, a1] CCW，剔除 [a1, a2]
      const a1 = BreakUtils.projectAngleOnEllipse(p1, entity)
      const a2 = BreakUtils.projectAngleOnEllipse(p2, entity)
      if (Math.abs(BreakUtils.normalizeAngle(a1 - a2)) < 1e-6) return results
      const newEntity = entity.clone() as EllipseEntity
      Object.assign(newEntity, { id: nanoid(), startAngle: a2, endAngle: a1 })
      results.createdEntities.push(newEntity)
      results.deleteEntities.push(entity)
      return results
    }

    // 椭圆弧：类似圆弧
    const clampToArc = (a: number): number => {
      if (BreakUtils.isAngleOnArc(a, arcStart, arcEnd)) return a
      const dStart = BreakUtils.ccwSpan(arcStart, a)
      const dEnd = BreakUtils.ccwSpan(a, arcEnd)
      return dStart <= dEnd ? arcStart : arcEnd
    }

    let a1 = clampToArc(BreakUtils.projectAngleOnEllipse(p1, entity))
    let a2 = clampToArc(BreakUtils.projectAngleOnEllipse(p2, entity))

    // 保证 a1 在 CCW 方向先于 a2（[a1, a2] 是要剔除的段）
    if (BreakUtils.ccwSpan(arcStart, a1) > BreakUtils.ccwSpan(arcStart, a2)) {
      ;[a1, a2] = [a2, a1]
    }

    const EPS = 1e-6

    // 前段 [arcStart, a1]
    if (BreakUtils.ccwSpan(arcStart, a1) > EPS) {
      const newEntity = entity.clone() as EllipseEntity
      Object.assign(newEntity, { id: nanoid(), startAngle: arcStart, endAngle: a1 })
      results.createdEntities.push(newEntity)
    }

    // 后段 [a2, arcEnd]
    if (BreakUtils.ccwSpan(a2, arcEnd) > EPS) {
      const newEntity = entity.clone() as EllipseEntity
      Object.assign(newEntity, { id: nanoid(), startAngle: a2, endAngle: arcEnd })
      results.createdEntities.push(newEntity)
    }

    if (results.createdEntities.length > 0) {
      results.deleteEntities.push(entity)
    }
    return results
  }

  // =========================================================================
  // Ray
  // =========================================================================

  /**
   * 将闭合折线裁剪到 [tStart, tEnd] 区间，返回一条新的开放折线实体。
   * 适用于修剪操作的"环绕段裁剪"（光标位于跨越 t=0/n 的段时，保留 [tStart, tEnd] 部分）。
   *
   * @param entity  要裁剪的闭合折线
   * @param tStart  起始参数（对应折线扩展坐标系中的 t 值，范围 [0, n]）
   * @param tEnd    终止参数
   * @returns 裁剪后的开放折线实体，若裁剪结果不足两点则返回 null
   */
  static clipClosedPolylineToInterval(
    entity: PolylineEntity,
    tStart: number,
    tEnd: number
  ): PolylineEntity | null {
    const vertices = entity.vertices
    if (vertices.length < 2) return null
    const extendedVertices: IPolylineVertice[] = [
      ...vertices,
      { x: vertices[0].x, y: vertices[0].y, bulge: 0 },
    ]
    const clipped = BreakUtils.clipPolylineSegment(extendedVertices, tStart, tEnd)
    if (clipped.length < 2) return null
    const newEntity = entity.clone() as PolylineEntity
    Object.assign(newEntity, { id: nanoid(), vertices: clipped, isClosed: false })
    return newEntity
  }

  /**
   * 打断射线（Ray）：
   *  - 将 p1、p2 投影到射线上，得到参数 t（t ≥ 0，t = dot(p - startPoint, unitVector)）
   *  - 剔除 [tStart, tEnd] 段
   *  - 前段 [0, tStart]（若 tStart > 0）→ 有限线段（PolylineEntity，2 顶点）
   *  - 后段 [tEnd, ∞) → 新的 RayEntity（起点平移到 tEnd 处）
   */
  private static breakRay(
    entity: RayEntity,
    p1: { x: number; y: number },
    p2: { x: number; y: number }
  ): BreakResult {
    const results: BreakResult = { createdEntities: [], deleteEntities: [] }
    const { startPoint, unitVector } = entity

    // 点投影到射线：t = dot(p - sp, uv)，夹紧到 ≥ 0
    const projectOnRay = (p: { x: number; y: number }): number =>
      Math.max(0, (p.x - startPoint.x) * unitVector.x + (p.y - startPoint.y) * unitVector.y)

    const t1 = projectOnRay(p1)
    const t2 = projectOnRay(p2)
    const tStart = Math.min(t1, t2)
    const tEnd = Math.max(t1, t2)

    if (tEnd - tStart < 1e-10) return results

    // 前段 [0, tStart]：有限线段（Polyline）
    if (tStart > 1e-6) {
      const seg = new PolylineEntity({
        id: nanoid(),
        layerId: entity.layerId,
        colorMethod: entity.colorMethod,
        color: entity.color,
        lineType: entity.lineType,
        lineWidth: entity.lineWidth,
        vertices: [
          { x: startPoint.x, y: startPoint.y, bulge: 0 },
          {
            x: startPoint.x + tStart * unitVector.x,
            y: startPoint.y + tStart * unitVector.y,
            bulge: 0,
          },
        ],
        isClosed: false,
      })
      results.createdEntities.push(seg)
    }

    // 后段 [tEnd, ∞)：新射线
    const newRay = entity.clone() as RayEntity
    Object.assign(newRay, {
      id: nanoid(),
      startPoint: {
        x: startPoint.x + tEnd * unitVector.x,
        y: startPoint.y + tEnd * unitVector.y,
      },
    })
    results.createdEntities.push(newRay)
    results.deleteEntities.push(entity)
    return results
  }
}
